// UTILS
import { computed, inject, ref } from "vue"
import { defineStore, storeToRefs } from "pinia"
import { MixpanelInstance } from "@/plugins/mixpanel/mixpanel"
import { getAudio } from "@/api/s3"
import { sleep } from "@/lib/utils"
import { initExtra, factoryTest, factoryItem, factoryStart } from "./brain.data"
import Bugsnag from "@bugsnag/js"
import { useToast } from "vue-toastification"

// TYPES
import type { Ref } from "vue"
import type { Extra } from "./brain.types"
import type { Translate } from "@/plugins/i18n/types"

// API
import {
  getAssessment,
  postAssessment,
  patchAssessment,
  deleteAssessment,
  getBrainNextItem,
} from "@/api/viktor/assessment/assessment"

// STORES
import { useI18nStore } from "@/stores/i18n"
import { useBrainTutoStore } from "./brainTuto.store"
import { useBrainLayoutStore } from "./brainLayout.store"
import { useUserStore } from "@/stores/user"

// MACHINE
import { brainActor } from "@/features/brain/machines/brainMachine"

export const useAnswersStore = defineStore("brainAnswersStore", () => {
  const t = inject("t") as Translate
  const { updateLocale } = useI18nStore()
  const { currentLocale, showLang } = storeToRefs(useI18nStore())
  const { isTutorial, currentTutoStep } = storeToRefs(useBrainTutoStore())
  const { allowActions } = storeToRefs(useBrainLayoutStore())
  const { showSkipCTA } = useBrainLayoutStore()
  const userStore = useUserStore()
  const { isFromInt } = storeToRefs(useUserStore())
  const { nextAssessment } = storeToRefs(useUserStore())
  const toast = useToast()

  const version = ref("1.0.0")

  // Ref
  const brainValues: Ref<null | any> = ref(null)
  const totalAnswers: Ref<number> = ref(0)
  const showMilestone: Ref<boolean> = ref(false)
  const extra: Ref<Extra> = ref(initExtra)
  const isPatchExtraLoading = ref(false)

  // Computed
  const getLanguage = computed(() =>
    currentLocale.value.iso_code === "ar"
      ? "ar_SA"
      : currentLocale.value.iso_code
  )

  /**
   * Submit the current step to the brain machine
   */
  function submitStep() {
    window.b.sendAnswer()
  }

  /**
   * Get the brain assessment
   */
  async function getBrain() {
    let data = await getAssessment("brain")
    const brainAssessment = userStore.assessmentSummary.assessments.brain
    const canRestart =
      brainAssessment?.can_restart ||
      brainAssessment?.retake_test ||
      (nextAssessment.value?.next_assessment.assessment === "brain" &&
        nextAssessment.value?.next_assessment.retake)

    if (
      brainAssessment?.status === "finish" &&
      !brainAssessment?.can_restart &&
      !brainAssessment?.retake_test &&
      !brainAssessment.not_passed
    ) {
      // si le test est terminé, on sort de l'assessment
      if (isFromInt.value) data.status = "thank-you"
      else window.location.reload()
    } else if (brainAssessment?.status === "finish" && canRestart) {
      // if brain status is finish and more than one year, init a new brain
      await deleteAssessment("brain")
      await initBrain(false, data.locale_id)
      data = await getAssessment("brain")
      resetBrainTest() // reset brain PIXI to get tutorial
    } else if (data.status === "inprogress" || !!brainAssessment?.not_passed) {
      // if test was created_at more than 24h, init a new brain to restart the test
      if (
        (await checkIfTestIsExpired(data.created_at)) &&
        isFromInt.value === false
      ) {
        await deleteAssessment("brain")
        await initBrain(false, data.locale_id)
        data = await getAssessment("brain")
        resetBrainTest() // reset brain PIXI to get tutorial
      } else {
        isTutorial.value = false
        // check if object data.data exists
        if (Object.keys(data?.data).length > 0 && data?.data?.answers) {
          totalAnswers.value = Object.keys(data?.data?.answers).length
        }
      }
    }

    brainValues.value = data
    extra.value = brainValues.value.data?.extra ?? initExtra

    return data
  }

  /**
   * Fetch the audios for the brain assessment
   */
  function fetchAudios() {
    getAudio({ test: "brain" })
  }

  /**
   * Check if the test is expired
   * @param timestamp - The timestamp of the test
   * @returns True if the test is expired, false otherwise
   */
  async function checkIfTestIsExpired(timestamp: number) {
    const now = new Date().getTime() / 1000
    const diffHours = (now - timestamp) / (60 * 60)
    if (diffHours > 23) {
      return true
    } else {
      return false
    }
  }

  /**
   * Initialize the brain assessment
   * @param restarting - Whether the test is being restarted
   * @param lang - The language ID
   */
  async function initBrain(restarting?: boolean | null, lang?: number | null) {
    if (restarting) extra.value.isRestarting = true
    const payload = {
      version: `${getLanguage.value}_${version.value}`,
      locale_id: lang ? lang : currentLocale.value.id,
      data: restarting ? { extra: { isRestarting: true } } : {},
    }

    try {
      await postAssessment("brain", payload)
    } catch (error) {
      console.error(error, payload)
      throw error
    }
  }

  /**
   * Start the brain assessment
   */
  async function startTest() {
    showLang.value = false

    if (totalAnswers.value < 1) {
      const payload = {
        version: `${getLanguage.value}_${version.value}`,
        locale_id: currentLocale.value.id,
        start: true,
      }
      const item = await patchAssessment("brain", payload)

      MixpanelInstance.track("brain_start", {
        payload: payload,
      })

      // push le first item
      window.b.data.push(item)
    } else {
      // push le next item
      const nextQuestion = await getBrainNextItem()
      window.b.data.push(nextQuestion)
    }
  }

  /**
   * Patch the brain assessment
   * @param data - The data to patch
   * @returns The patched assessment
   */
  async function patchTest(data) {
    const payload = {
      version: `${getLanguage.value}_${version.value}`,
      locale_id: currentLocale.value.id,
      data: { ...data },
    }
    try {
      return await patchAssessment("brain", payload)
    } catch (error) {
      console.error(error, payload)
      throw error
    }
  }

  /**
   * Show the breath screen
   */
  async function showBreath() {
    totalAnswers.value++
    // if totalAnswser is 1 or 5, show the breath screen
    if (totalAnswers.value === 1 || totalAnswers.value === 5) {
      allowActions.value = false
      showMilestone.value = true
    }
  }

  /**
   * Patch the extra data for the brain assessment
   * @param type - The type of extra data to patch
   * @param answer - The answer to patch
   */
  async function patchExtra(type: string, answer: boolean) {
    isPatchExtraLoading.value = true
    try {
      extra.value[type] = answer
    } finally {
      await sleep(300) // Timeout to prevent flicker
      isPatchExtraLoading.value = false
    }
  }

  /**
   * Get the elapsed time for the brain assessment
   * @returns The elapsed time in seconds
   */
  async function getElapsedTime() {
    let time = 0
    const data = await getAssessment("brain")
    const answers = data.data.answers
    const arrayAnswers = Object.keys(answers)
    arrayAnswers.map((item) => (time += answers[item].time_initial))
    return Math.round(time)
  }

  /**
   * Patch the restart data for the brain assessment
   * @param type - The type of restart data to patch
   * @param answer - The answer to patch
   * @param lang - The language ID
   */
  async function patchRestart(
    type: string,
    answer: boolean,
    lang?: number | null
  ) {
    // then, if user wants to restart,
    if (answer) {
      // Submit final results before deleting the assessment
      try {
        await submitFinalResults()
      } catch (error) {
        Bugsnag.notify(error, (event) => {
          event.context = "BrainAnswersStore.patchRestart"
          event.severity = "error"
          event.unhandled = false
          event.addMetadata("request", {
            email: userStore.user?.email || "",
            lang: lang,
            type: type,
            answer: JSON.stringify({
              status: brainValues.value?.status,
              data: {
                ...brainValues.value?.data,
                items: brainValues.value?.data?.items?.length ?? 0,
              },
            }),
          })
        })
      }

      try {
        // delete active brain
        await deleteAssessment("brain")

        // reset tutorial
        await resetBrainTest()

        // init a brand new assessment with isRestarting param = true
        // isRestarting?: boolean, lang?: number | null
        await initBrain(true, lang ? lang : null)
        lang && (await updateLocale(lang))
        await userStore.fetchAssessmentSummary()

        // finally, send user to the right state (now or later)
        if (type === "now") {
          return brainActor.send({ type: "now" })
        } else {
          return brainActor.send({ type: "later" })
        }
      } catch (error) {
        console.log("Error while deleting brain assessment", error)
        toast.error(t("general.error.general"))
        throw error
      }
    } else {
      // BrainLoadResults will call submitFinalResults() on mount
      return brainActor.send({ type: "done" })
    }
  }

  /**
   * Reset the brain test
   */
  async function resetBrainTest() {
    isTutorial.value = true
    currentTutoStep.value = 0
    totalAnswers.value = 0
    showSkipCTA()
  }

  /**
   * Submit the final results for the brain assessment
   * @returns The submitted assessment
   */
  async function submitFinalResults() {
    extra.value.elapsed_time = await getElapsedTime()
    extra.value.finished = true
    const payload = {
      version: `${getLanguage.value}_${version.value}`,
      locale_id: currentLocale.value.id,
      data: {
        extra: extra.value,
      },
    }
    MixpanelInstance.track("brain_finish", {
      payload: payload,
    })
    return await patchAssessment("brain", payload)
  }

  /**
   * Patch the factory test for the brain assessment
   * @param isRestarting - Whether the test is being restarted
   */
  async function patchFactoryTest(isRestarting: boolean) {
    try {
      await deleteAssessment("brain")
    } catch {
      return
    }

    await initBrain(isRestarting)
    await patchAssessment("brain", factoryStart)

    for (const item of factoryItem) {
      // Patch responses
      const payload = factoryTest(item)
      await patchAssessment("brain", payload)
    }
    window.location.reload()
  }

  /**
   * Reset the user data for the brain assessment
   */
  async function resetUserDataLight() {
    try {
      await deleteAssessment("brain")
    } catch {
      return
    }
    await resetBrainTest()
    window.location.reload()
  }

  return {
    brainValues,
    extra,
    totalAnswers,
    showMilestone,
    initBrain,
    getBrain,
    fetchAudios,
    startTest,
    submitStep,
    patchTest,
    showBreath,
    patchExtra,
    isPatchExtraLoading,
    patchRestart,
    submitFinalResults,
    patchFactoryTest,
    resetUserDataLight,
  }
})
